import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { uploadMediaFile } from '@/hooks/useUpload';
import { eventImage, eventDateLabel, isEventPast, isEventPaid, type PublicEvent } from '@/lib/publicEvents';
import { fetchEventRegistrations, type EventRegistration } from '@/lib/eventRegistrations';
import { buildSubdomain } from '@/lib/domain';

type Filter = 'all' | 'published' | 'draft' | 'upcoming';

interface FormState {
  id: number | null;
  title: string;
  description: string;
  start: string;
  end: string;
  image: string;
  published: boolean;
  /** Nombre total de places (vide = illimité). */
  capacity: string;
  /** Prix d'entrée (vide ou 0 = gratuit). */
  price: string;
  currency: string;
  /** Lieu de l'événement (facultatif). */
  location: string;
}

const EMPTY_FORM: FormState = {
  id: null,
  title: '',
  description: '',
  start: '',
  end: '',
  image: '',
  published: true,
  capacity: '',
  price: '',
  currency: 'EUR',
  location: '',
};

function toInputValue(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function DashboardEventsPage() {
  const { user } = useAuth();
  /** Page publique des événements : TOUJOURS sur le sous-domaine du commerce. */
  const publicEventsUrl = user?.user_name ? `${buildSubdomain(user.user_name).replace(/\/+$/, '')}/events` : '';
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [imageBusy, setImageBusy] = useState(false);
  const [deleting, setDeleting] = useState<PublicEvent | null>(null);
  const [feedback, setFeedback] = useState('');
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [regModal, setRegModal] = useState<{ event: PublicEvent; rows: EventRegistration[]; loading: boolean } | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('events')
        .select('id, title, description, start_datetime, end_datetime, status, id_user, code, id_haut, id_milieu, id_bas, capacity, registration_open, price, currency, location')
        .eq('id_user', String(user.id))
        .order('start_datetime', { ascending: false });
      if (err) throw err;
      setEvents((data || []) as PublicEvent[]);
      // Inscriptions de tous les événements du commerce (pour les compteurs + l'export CSv).
      const { data: regs } = await supabase
        .from('event_registrations')
        .select('*')
        .eq('merchant_id', String(user.id))
        .order('created_at', { ascending: false });
      setRegistrations((regs || []) as EventRegistration[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les événements.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    return events.filter((ev) => {
      const published = ev.status == null || Number(ev.status) === 1;
      if (filter === 'published') return published;
      if (filter === 'draft') return !published;
      if (filter === 'upcoming') return published && !isEventPast(ev);
      return true;
    });
  }, [events, filter]);

  /** Places réservées par événement (somme des sièges). */
  const seatsByEvent = useMemo(() => {
    const map: Record<number, number> = {};
    registrations.forEach((r) => {
      if (String(r.status || 'confirmed') === 'cancelled') return;
      map[r.event_id] = (map[r.event_id] || 0) + (Number(r.seats) || 1);
    });
    return map;
  }, [registrations]);

  const openRegistrations = async (ev: PublicEvent) => {
    setRegModal({ event: ev, rows: [], loading: true });
    try {
      const rows = await fetchEventRegistrations(ev.id);
      setRegModal({ event: ev, rows, loading: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les inscrits.');
      setRegModal({ event: ev, rows: [], loading: false });
    }
  };

  /** Export CSV des inscrits d'un événement (compatible Excel). */
  const exportRegistrationsCsv = (ev: PublicEvent, rows: EventRegistration[]) => {
    const esc = (value: unknown) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const header = ['Nom', 'Email', 'Téléphone', 'Places', 'Message', 'Statut', 'Date'];
    const lines = rows.map((r) => [
      r.fullname,
      r.email || '',
      r.phone || '',
      r.seats || 1,
      r.note || '',
      r.status || 'confirmed',
      r.created_at ? new Date(r.created_at).toLocaleString('fr-FR') : '',
    ].map(esc).join(';'));
    const csv = `\uFEFF${[header.map(esc).join(';'), ...lines].join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safe = (ev.title || 'evenement').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    a.download = `inscrits-${safe || ev.id}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openCreate = () => {
    setFormError('');
    setForm({ ...EMPTY_FORM, currency: user?.monaie || 'EUR' });
  };
  const openEdit = (ev: PublicEvent) => {
    setFormError('');
    setForm({
      id: ev.id,
      title: ev.title || '',
      description: ev.description || '',
      start: toInputValue(ev.start_datetime),
      end: toInputValue(ev.end_datetime),
      image: eventImage(ev),
      published: ev.status == null || Number(ev.status) === 1,
      capacity: ev.capacity != null ? String(ev.capacity) : '',
      price: ev.price != null && Number(ev.price) > 0 ? String(ev.price) : '',
      currency: ev.currency || user?.monaie || 'EUR',
      location: ev.location || '',
    });
  };

  const handleImage = async (file: File | undefined) => {
    if (!file || !form) return;
    setImageBusy(true);
    setFormError('');
    try {
      const url = await uploadMediaFile(file, 'events');
      setForm((prev) => (prev ? { ...prev, image: url } : prev));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "L'image n'a pas pu être envoyée.");
    } finally {
      setImageBusy(false);
    }
  };

  const save = async () => {
    if (!form || !user) return;
    if (!form.title.trim()) { setFormError("Le titre de l'événement est obligatoire."); return; }
    if (!form.start) { setFormError('Indiquez la date et l’heure de début.'); return; }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        title: form.title.trim(),
        // La table `events` exige une description et une date de fin : on
        // fournit des valeurs sûres pour éviter « L'événement n'a pas pu être enregistré ».
        description: form.description.trim(),
        start_datetime: form.start,
        end_datetime: form.end || form.start,
        status: form.published ? 1 : 0,
        id_user: String(user.id),
        id_haut: form.image || '',
        id_milieu: '',
        id_bas: '',
        code: '',
        capacity: form.capacity.trim() ? Math.max(1, Number(form.capacity)) : null,
        registration_open: true,
        price: form.price.trim() ? Math.max(0, Number(form.price)) : null,
        currency: form.currency || 'EUR',
        location: form.location.trim() || null,
      };
      if (form.id) {
        const { error: err } = await supabase.from('events').update(payload).eq('id', form.id);
        if (err) throw err;
      } else {
        const { error: err } = await supabase.from('events').insert(payload);
        if (err) throw err;
      }
      setForm(null);
      setFeedback(form.id ? 'Événement mis à jour.' : 'Événement créé.');
      window.setTimeout(() => setFeedback(''), 3200);
      await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "L'événement n'a pas pu être enregistré.");
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (ev: PublicEvent) => {
    const next = ev.status != null && Number(ev.status) === 1 ? 0 : 1;
    const { error: err } = await supabase.from('events').update({ status: next }).eq('id', ev.id);
    if (err) {
      setError(err.message);
      return;
    }
    setEvents((prev) => prev.map((e) => (e.id === ev.id ? { ...e, status: next } : e)));
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    const { error: err } = await supabase.from('events').delete().eq('id', deleting.id);
    if (err) {
      setError(err.message);
      setDeleting(null);
      return;
    }
    setEvents((prev) => prev.filter((e) => e.id !== deleting.id));
    setDeleting(null);
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-calendar-event-line text-primary-500"></i>Événements
          </h1>
          <p className="text-sm text-foreground-500 mt-1">
            Alimentez la page publique « Événements » et la balise {'{{events}}'} de votre site.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-5 h-11 bg-primary-500 text-background-50 rounded-xl text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line text-base"></i>Nouvel événement
          </button>
          {publicEventsUrl ? (
            <a
              href={publicEventsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 h-11 bg-background-100 border border-background-200/70 text-foreground-700 rounded-xl text-sm font-medium whitespace-nowrap hover:bg-background-200/70 transition-colors no-underline"
            >
              <i className="ri-external-link-line"></i>Voir la page publique
            </a>
          ) : (
            <span className="flex items-center gap-2 px-4 h-11 bg-background-100 border border-background-200/70 text-foreground-400 rounded-xl text-sm font-medium whitespace-nowrap">
              <i className="ri-external-link-line"></i>Voir la page publique
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full w-fit mb-5 overflow-x-auto max-w-full">
        {([
          { key: 'all' as Filter, label: 'Tous' },
          { key: 'published' as Filter, label: 'Publiés' },
          { key: 'draft' as Filter, label: 'Brouillons' },
          { key: 'upcoming' as Filter, label: 'À venir' },
        ]).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer transition-colors ${
              filter === tab.key ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {feedback && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-accent-50 border border-accent-200/70 text-xs text-accent-800 flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>{feedback}
        </div>
      )}
      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
          <button onClick={() => setError('')} className="ml-auto cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg text-center">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-calendar-line text-xl text-foreground-400"></i>
          </div>
          <h3 className="text-sm font-semibold text-foreground-800 mb-1">Aucun événement</h3>
          <p className="text-xs text-foreground-500 max-w-sm mb-4">
            Créez votre premier événement : il apparaîtra dans votre agenda public.
          </p>
          <button
            onClick={openCreate}
            className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
          >
            Créer un événement
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((ev) => {
            const cover = eventImage(ev);
            const published = ev.status == null || Number(ev.status) === 1;
            return (
              <div key={ev.id} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden flex flex-col">
                <div className="h-36 bg-background-100 overflow-hidden relative">
                  {cover ? (
                    <img src={cover} alt={ev.title || 'Événement'} className="w-full h-full object-cover object-top" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-secondary-100 to-accent-50">
                      <i className="ri-calendar-event-line text-4xl text-secondary-300"></i>
                    </div>
                  )}
                  <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    published ? 'bg-accent-500 text-background-50' : 'bg-background-950/70 text-background-50'
                  }`}>
                    {published ? 'Publié' : 'Brouillon'}
                  </span>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2">{ev.title || 'Événement'}</h3>
                  <p className="text-xs text-foreground-500 mt-1.5 flex items-center gap-1.5">
                    <i className="ri-calendar-line"></i>
                    <span className="truncate">{eventDateLabel(ev.start_datetime)}</span>
                  </p>
                  {ev.location && (
                    <p className="text-xs text-foreground-500 mt-1 flex items-center gap-1.5">
                      <i className="ri-map-pin-line"></i>
                      <span className="truncate">{ev.location}</span>
                    </p>
                  )}
                  {ev.description && (
                    <p className="text-xs text-foreground-600 line-clamp-2 mt-1.5">{ev.description}</p>
                  )}
                  {(() => {
                    const seats = seatsByEvent[ev.id] || 0;
                    const cap = ev.capacity != null ? Number(ev.capacity) : null;
                    const isFull = cap != null && seats >= cap;
                    return (
                      <div className="mt-2.5 flex flex-wrap items-center gap-1.5 text-[11px]">
                        {isEventPaid(ev) ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-900 font-semibold">
                            <i className="ri-price-tag-3-line"></i>
                            {Number(ev.price).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {ev.currency || 'EUR'}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 font-semibold">
                            <i className="ri-gift-line"></i>Gratuit
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 font-semibold">
                          <i className="ri-group-line"></i>{seats} inscrit{seats > 1 ? 's' : ''}{cap != null ? ` / ${cap} places` : ''}
                        </span>
                        {isFull && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-semibold">
                            <i className="ri-lock-line"></i>Complet
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  <div className="mt-auto pt-4 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => void openRegistrations(ev)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-group-line"></i>Inscrits
                    </button>
                    <button
                      onClick={() => openEdit(ev)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-edit-line"></i>Modifier
                    </button>
                    <button
                      onClick={() => void togglePublished(ev)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={published ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
                      {published ? 'Dépublier' : 'Publier'}
                    </button>
                    <button
                      onClick={() => setDeleting(ev)}
                      className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer ml-auto"
                      aria-label="Supprimer"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Formulaire */}
      {form && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => (saving ? undefined : setForm(null))}></div>
          <div className="relative w-full max-w-lg bg-background-50 rounded-xl border border-background-200/70 max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 sticky top-0 bg-background-50 z-10">
              <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                <i className="ri-calendar-event-line text-primary-500"></i>
                {form.id ? "Modifier l'événement" : 'Nouvel événement'}
              </h3>
              <button
                onClick={() => (saving ? undefined : setForm(null))}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                  Titre <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex : Soirée d'ouverture, Atelier cuisine…"
                  className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                    Début <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    value={form.start}
                    onChange={(e) => setForm({ ...form, start: e.target.value })}
                    className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Fin (optionnel)</label>
                  <input
                    type="datetime-local"
                    value={form.end}
                    onChange={(e) => setForm({ ...form, end: e.target.value })}
                    className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                  Nombre de places <span className="text-foreground-400 font-normal">(vide = illimité)</span>
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.capacity}
                  onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                  placeholder="Ex : 50"
                  className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                />
                <p className="text-[11px] text-foreground-400 mt-1">
                  Les inscriptions se ferment automatiquement quand toutes les places sont prises.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                    Prix d&apos;entrée <span className="text-foreground-400 font-normal">(vide = gratuit)</span>
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="Ex : 15"
                    className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Devise</label>
                  <select
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer"
                  >
                    {['EUR', 'XOF', 'XAF', 'MAD', 'USD', 'GBP', 'CHF', 'CAD', 'DZD', 'TND', 'NGN', 'GHS', 'KES'].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-[11px] text-foreground-400 -mt-1">
                Un prix renseigné permet au visiteur de payer en ligne (si le paiement est activé par la plateforme) ou de réserver en réglant sur place.
              </p>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Lieu (optionnel)</label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                  placeholder="Ex : Salle polyvalente, 12 rue des Arts"
                  className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Image de l&apos;événement</label>
                {form.image ? (
                  <div className="flex items-center gap-3">
                    <img src={form.image} alt="Visuel" className="w-20 h-14 rounded-md object-cover object-top flex-shrink-0" />
                    <button
                      onClick={() => setForm({ ...form, image: '' })}
                      className="px-3 py-1.5 rounded-full bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-200/70 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line mr-1"></i>Retirer
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 h-11 rounded-lg border border-dashed border-background-300 bg-background-50 text-xs font-medium text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors">
                    {imageBusy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-image-add-line text-base"></i>}
                    {imageBusy ? 'Envoi…' : 'Choisir une image'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => void handleImage(e.target.files?.[0])}
                    />
                  </label>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4}
                  maxLength={500}
                  placeholder="Programme, lieu, informations pratiques…"
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 resize-none"
                />
                <p className="text-[11px] text-foreground-400 mt-1">{form.description.length}/500</p>
              </div>

              <button
                type="button"
                onClick={() => setForm({ ...form, published: !form.published })}
                className="w-full flex items-center justify-between gap-3 p-3 rounded-lg border border-background-200/70 bg-background-100/60 cursor-pointer"
              >
                <span className="flex items-center gap-2 text-sm text-foreground-800">
                  <i className={form.published ? 'ri-eye-line text-accent-600' : 'ri-eye-off-line text-foreground-400'}></i>
                  {form.published ? 'Publié sur le site' : 'Brouillon (invisible)'}
                </span>
                <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.published ? 'bg-accent-500' : 'bg-background-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.published ? 'translate-x-6' : 'translate-x-1'}`} />
                </span>
              </button>

              {formError && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>
              )}
            </div>

            <div className="px-5 pb-5 pt-1 sticky bottom-0 bg-background-50">
              <button
                onClick={() => void save()}
                disabled={saving}
                className="w-full h-12 rounded-full bg-primary-500 text-background-50 text-sm font-bold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                {form.id ? 'Enregistrer les modifications' : "Créer l'événement"}
              </button>
            </div>
          </div>
        </div>
      )}

      {regModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setRegModal(null)}></div>
          <div className="relative w-full max-w-2xl bg-background-50 rounded-xl border border-background-200/70 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between gap-3 px-5 h-14 border-b border-background-200/70 flex-shrink-0">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950 truncate">Inscrits — {regModal.event.title || 'Événement'}</h3>
                <p className="text-[11px] text-foreground-500">
                  {regModal.rows.reduce((s, r) => s + (Number(r.seats) || 1), 0)} place(s) réservée(s)
                  {regModal.event.capacity != null ? ` sur ${regModal.event.capacity}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => exportRegistrationsCsv(regModal.event, regModal.rows)}
                  disabled={regModal.rows.length === 0}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-secondary-600 transition-colors cursor-pointer disabled:opacity-40"
                >
                  <i className="ri-file-excel-2-line"></i>CSV
                </button>
                <button onClick={() => setRegModal(null)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer">
                  <i className="ri-close-line text-foreground-500"></i>
                </button>
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {regModal.loading ? (
                <div className="flex items-center justify-center py-16">
                  <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
                </div>
              ) : regModal.rows.length === 0 ? (
                <div className="py-14 text-center px-6">
                  <i className="ri-group-line text-3xl text-foreground-300"></i>
                  <p className="text-sm text-foreground-600 mt-2">Aucune inscription pour le moment.</p>
                  <p className="text-xs text-foreground-400 mt-1">Les visiteurs peuvent s&apos;inscrire depuis la page publique de l&apos;événement.</p>
                </div>
              ) : (
                <div className="divide-y divide-background-200/70">
                  {regModal.rows.map((r) => (
                    <div key={r.id} className="flex items-start gap-3 px-4 py-3">
                      <span className="w-9 h-9 rounded-full bg-secondary-100 text-secondary-800 flex items-center justify-center flex-shrink-0 text-xs font-bold uppercase">
                        {(r.fullname || '?').trim().charAt(0)}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-foreground-900 truncate">{r.fullname}</p>
                        <p className="text-[11px] text-foreground-500 truncate">
                          {[r.email, r.phone].filter(Boolean).join(' · ') || 'Aucun contact'}
                        </p>
                        {r.note && <p className="text-[11px] text-foreground-600 mt-0.5 line-clamp-2">{r.note}</p>}
                        <p className="text-[10px] text-foreground-400 mt-0.5">{r.created_at ? new Date(r.created_at).toLocaleString('fr-FR') : ''}</p>
                      </div>
                      <span className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-700 text-[10px] font-semibold whitespace-nowrap">
                        {r.seats || 1} place{(r.seats || 1) > 1 ? 's' : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleting(null)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl border border-background-200/70 p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line text-red-500"></i>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-950">Supprimer cet événement ?</h3>
                <p className="text-xs text-foreground-500 mt-0.5">« {deleting.title || 'Événement'} » sera retiré de votre agenda.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setDeleting(null)}
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                onClick={() => void confirmDelete()}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white hover:bg-red-600 cursor-pointer whitespace-nowrap"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}